import React, { useState, useEffect } from 'react';
import { Volume2, CheckCircle, XCircle, Star, Trophy } from 'lucide-react';
import { useApp } from '../../lib/AppContext';
import { getRandomWords } from '../../lib/vocabulary';
import { speechService } from '../../services/speechService';
import { VocabularyWord } from '../../types';

const WORDS_PER_GAME = 10;
const POINTS_PER_WORD = 10;

export function WordMatchGame() {
  const { gameState, addScore, markWordLearned, showCelebration, endGame } = useApp();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [options, setOptions] = useState<VocabularyWord[]>([]);
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const currentSession = gameState.currentSession;

  // Initialize game
  useEffect(() => {
    if (currentSession) {
      const gameWords = getRandomWords(
        WORDS_PER_GAME,
        currentSession.level,
        currentSession.language
      );
      setWords(gameWords);
      setCurrentWordIndex(0);
      setScore(0);
      setCorrectCount(0);
    }
  }, [currentSession]);

  // Set up options when current word changes
  useEffect(() => {
    if (words.length > 0 && currentWordIndex < words.length) {
      const currentWord = words[currentWordIndex];
      setupOptions(currentWord);

      // Note: Auto-play removed - browsers block speech synthesis unless triggered by user interaction
      // Users must click the "Play Word" button to hear the word
    }
  }, [currentWordIndex, words]);

  const setupOptions = (correctWord: VocabularyWord) => {
    if (!currentSession) return;

    // Get 3 random wrong words from the same level and language
    const allWords = getRandomWords(20, currentSession.level, currentSession.language);
    const wrongWords = allWords
      .filter((w) => w.id !== correctWord.id)
      .slice(0, 3);

    // Combine correct word with wrong words and shuffle
    const allOptions = [correctWord, ...wrongWords].sort(() => Math.random() - 0.5);
    setOptions(allOptions);
  };

  const playWord = () => {
    if (words.length === 0 || currentWordIndex >= words.length || !currentSession) return;

    const currentWord = words[currentWordIndex];
    setIsSpeaking(true);

    speechService.speak(currentWord.word, currentSession.language, {
      rate: 0.8,
      onEnd: () => setIsSpeaking(false),
      onStart: () => setIsSpeaking(true),
    });
  };

  const handleWordSelect = (word: VocabularyWord) => {
    if (selectedWord || !currentSession) return;

    setSelectedWord(word.id);

    const currentWord = words[currentWordIndex];
    const correct = word.id === currentWord.id;
    setIsCorrect(correct);

    if (correct) {
      const points = POINTS_PER_WORD;
      setScore(score + points);
      setCorrectCount(correctCount + 1);
      addScore(points);
      markWordLearned(currentWord.id);
    }

    // Move to next word after delay
    setTimeout(() => {
      if (currentWordIndex + 1 >= words.length) {
        // Game complete
        handleGameComplete();
      } else {
        setCurrentWordIndex(currentWordIndex + 1);
        setSelectedWord(null);
        setIsCorrect(null);
      }
    }, 1500);
  };

  const handleGameComplete = () => {
    showCelebration();

    setTimeout(() => {
      endGame();
    }, 3000);
  };

  if (!currentSession || words.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading game...</div>
      </div>
    );
  }

  const currentWord = words[currentWordIndex];
  const progress = ((currentWordIndex + 1) / words.length) * 100;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-3xl font-bold text-primary flex items-center gap-2">
              ✨ Word Match
            </h2>
            <p className="text-gray-600 mt-1">Listen and find the word!</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <Star className="text-yellow-500 fill-yellow-500" />
              <span className="text-3xl font-bold text-yellow-600">{score}</span>
            </div>
            <p className="text-sm text-gray-500">Score</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute h-full bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="text-sm text-gray-600 mt-2 text-center">
          Question {currentWordIndex + 1} of {words.length}
        </div>
      </div>

      {/* Sound Button */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-6 text-center">
        <div className="mb-6">
          <button
            onClick={playWord}
            disabled={isSpeaking}
            className={`btn-primary text-xl px-12 py-6 flex items-center gap-3 mx-auto ${
              isSpeaking ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            <Volume2 className={isSpeaking ? 'animate-pulse' : ''} size={32} />
            {isSpeaking ? 'Playing...' : 'Play Word'}
          </button>
        </div>

        {/* Feedback */}
        {isCorrect !== null && (
          <div
            className={`animate-fadeIn text-center p-4 rounded-2xl ${
              isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2 text-xl font-bold">
              {isCorrect ? (
                <>
                  <CheckCircle size={24} />
                  Correct! +{POINTS_PER_WORD} points
                </>
              ) : (
                <>
                  <XCircle size={24} />
                  Try the next one!
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Word Options */}
      <div className="grid grid-cols-2 gap-4">
        {options.map((word) => {
          const isSelected = selectedWord === word.id;
          const isCurrentCorrect = word.id === currentWord.id;
          const showCorrect = selectedWord && isCurrentCorrect;
          const showWrong = isSelected && !isCorrect;

          return (
            <button
              key={word.id}
              onClick={() => handleWordSelect(word)}
              disabled={selectedWord !== null}
              className={`
                p-8 rounded-2xl text-2xl font-bold transition-all duration-300
                ${selectedWord === null ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed'}
                ${
                  showCorrect
                    ? 'bg-green-500 text-white shadow-lg'
                    : showWrong
                    ? 'bg-red-500 text-white shadow-lg'
                    : 'bg-white text-gray-800 shadow-md hover:shadow-xl'
                }
              `}
            >
              {word.word}
              {showCorrect && <CheckCircle className="inline-block ml-2" size={28} />}
              {showWrong && <XCircle className="inline-block ml-2" size={28} />}
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="bg-white rounded-3xl shadow-lg p-6 mt-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-blue-600">{correctCount}</div>
            <div className="text-sm text-gray-600">Correct</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-red-600">
              {currentWordIndex + 1 - correctCount}
            </div>
            <div className="text-sm text-gray-600">Wrong</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-600">
              {Math.round((correctCount / (currentWordIndex + 1)) * 100)}%
            </div>
            <div className="text-sm text-gray-600">Accuracy</div>
          </div>
        </div>
      </div>
    </div>
  );
}
